import React, { FC, useEffect } from 'react';

import clsx from 'clsx';

import Api from '~/Api';
import Button from '~/components/atoms/buttons/Button';
import { useModalContext } from '~/components/layoutComponents/modal/ModalContext';
import { useSnackBarContext } from '~/components/layoutComponents/snackbar/SnackbarContext';
import Pagination from '~/components/molecules/Pagination';
import TopHeadlineRow from '~/components/molecules/TopHeadlineRow';
import TotalCount from '~/components/molecules/TotalCount';
import useFilters from '~/hooks/useFilters';
import useQueryParams from '~/hooks/useQueryParams';
import CreateForm from '~/modules/groupsAffirmationsCategories/components/GroupAffirmationCategoryList/CreateForm';
import Table from '~/modules/groupsAffirmationsCategories/components/GroupAffirmationCategoryList/Table';
import useData, {
  FiltersType,
} from '~/modules/groupsAffirmationsCategories/components/GroupAffirmationCategoryList/useData';
import { SORT_ORDER } from '~/types/common';

import styles from './styles.modules.scss';

const DEFAULT_FILTER_STATE: FiltersType = {
  sortOrder: SORT_ORDER.DESC,
  sortBy: 'created_at',
  page: 1,
  'per-page': 10,
};

const GroupAffirmationCategoryList: FC = () => {
  const { handleOpenModal } = useModalContext();
  const { showSnackbar } = useSnackBarContext();
  const { getQueryParams, setQueryParams } = useQueryParams();

  const defaultFilterState = {
    ...DEFAULT_FILTER_STATE,
    ...getQueryParams(),
  };

  const { filters, updateFilter } = useFilters<FiltersType>(defaultFilterState);

  const { groups, isLoading, meta, updateItems } = useData(filters);

  useEffect(() => {
    setQueryParams(filters);
  }, [filters]);

  const handleAdd = async (data: {
    name: string;
    improvement_area_id: string;
  }): Promise<void> => {
    try {
      await Api.createGroupAffirmationCategory(data);
      await updateItems();
      showSnackbar('Group created', 'success');
    } catch (e: any) {
      showSnackbar(e.message);
      console.error(e);
    }
  };

  return (
    <div
      className={clsx(
        { [styles.loading]: isLoading },
        styles.vertical_flex_container,
        styles['vertical_flex_container--full_height'],
      )}
    >
      <TopHeadlineRow className={styles.top_bar} text="Improvement areas">
        <Button
          variant="outlined"
          onClick={(): void => {
            handleOpenModal({
              component: ({ onClose }) => (
                <CreateForm addGroup={handleAdd} onClose={onClose} />
              ),
            });
          }}
        >
          New Group
        </Button>
      </TopHeadlineRow>
      <div
        className={clsx(
          styles.container,
          styles.vertical_flex_container__full_parent_height,
        )}
      >
        <div className={styles.container__left}>
          <TotalCount count={meta.totalCount} />
          <div
            className={clsx(
              styles.vertical_flex_container,
              styles['vertical_flex_container--full_height'],
            )}
          >
            <div className={styles.vertical_flex_container__full_parent_height}>
              <Table
                groups={groups}
                filter={{
                  sortBy: filters.sortBy,
                  sortOrder: filters.sortOrder,
                }}
                applyFilters={(filters): void => updateFilter(filters)}
              />
            </div>
            {meta.totalPages > 1 && (
              <Pagination
                currentPage={Number(meta.currentPage)}
                totalPages={Number(meta.totalPages)}
                setPage={(page: number): void => {
                  updateFilter({ page });
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupAffirmationCategoryList;
